// src/pages/clab-selection-requests/index.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/ui/HeaderBar";
import RoleBasedSidebar from "../../components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import Icon from "../../components/AppIcon";
import { useClabSelectionRequests } from "../../hooks/useClabSelectionRequests";
import { useApplicationActions } from "../../hooks/useApplicationActions";
import { usePagination } from "../../hooks/usePagination";
import { CLAB_SELECTION_TABS, DEFAULT_TAB } from "../../constants/clabSelectionTabs";
import ApproveModal from "./components/ApproveModal";
import RejectModal from "./components/RejectModal";
import SelectionRequestTable from "./SelectionRequestTable";
import Notifier from "../../components/ui/Notifier";

const ClabSelectionRequests = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const [notification, setNotification] = useState({ show: false, type: "info", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("all");
  
  // Pagination hook
  const {
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    resetToFirstPage,
  } = usePagination(1, 10);

  const isApprovedTab = activeTab === "approved";

  // Debounce search term (Approved tab only)
  useEffect(() => {
    if (!isApprovedTab) {
      setDebouncedSearchTerm("");
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [isApprovedTab, searchTerm]);

  // Reset to first page when search changes (Approved tab only)
  useEffect(() => {
    if (!isApprovedTab) return;
    resetToFirstPage();
  }, [debouncedSearchTerm, searchBy, isApprovedTab, resetToFirstPage]);

  // Clear Approved tab search when leaving the tab
  useEffect(() => {
    if (isApprovedTab) return;
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSearchBy("all");
  }, [isApprovedTab]);
  
  // Custom hooks for data and actions
  const {
    applications,
    rejectionReasons,
    loading,
    error,
    pagination,
    refreshData,
    statusCounts,
  } = useClabSelectionRequests(
    activeTab,
    currentPage,
    itemsPerPage,
    isApprovedTab ? debouncedSearchTerm : "",
    isApprovedTab ? searchBy : "all"
  );
  
  // Success and error handlers
  const handleSuccess = useCallback((message) => {
    setNotification({ show: true, type: 'success', message });
    refreshData();
  }, [refreshData]);
  
  const handleError = useCallback((message) => {
    console.error('Error:', message);
    setNotification({ show: true, type: 'error', message });
  }, []);
  
  const {
    processing,
    showApproveModal,
    showRejectModal,
    selectedApplication,
    rejectionForm,
    handleApprove,
    handleReject,
    confirmApprove,
    confirmReject,
    cancelApprove,
    cancelReject,
    updateRejectionForm,
  } = useApplicationActions(handleSuccess, handleError);

  // Handle tab change
  const handleTabChange = useCallback((tabKey) => {
    setActiveTab(tabKey);
  }, []);

  // Handle view details
  const handleViewDetails = useCallback((application) => {
    navigate(`/clab-selection-requests/${application.id}`);
  }, [navigate]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refreshData();
  }, [refreshData]);

  const getTabCount = (tabKey) => {
    if (!statusCounts) return 0;
    return statusCounts[tabKey] || 0;
  };

  const breadcrumbItems = [
    { label: "Dashboard", href: "/dashboard" },
    { label: "CLAB Selection Requests", href: "/clab-selection-requests" },
  ];

  if (loading && applications.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderBar
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
        />
        <div className="flex">
          <RoleBasedSidebar 
            isCollapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
          <main className={`flex-1 transition-all duration-300 ${
            sidebarCollapsed ? "ml-16" : "ml-64"
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading applications...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderBar
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
      <Notifier
        type={notification.type}
        message={notification.message}
        show={notification.show}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
        duration={3000}
      />
      <div className="flex">
        <RoleBasedSidebar 
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className={`flex-1 transition-all duration-300 ${
          sidebarCollapsed ? "ml-16" : "ml-64"
        }`}>
          <div className="p-6 pt-20">
            {/* Header Section */}
            <div className="mb-6">
              <BreadcrumbNavigation items={breadcrumbItems} />
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4">
                <div className="mb-4 sm:mb-0">
                  <h1 className="text-2xl font-bold text-gray-900">
                    CLAB Selection Requests
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Manage and review application selection requests
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleRefresh}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon
                      name="RefreshCw"
                      className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {CLAB_SELECTION_TABS.map((tab) => {
                    const isActive = activeTab === tab.key;
                    const count = getTabCount(tab.key);
                    
                    return (
                      <button
                        key={tab.key}
                        onClick={() => handleTabChange(tab.key)}
                        className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                          isActive
                            ? "border-primary-500 text-primary-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <Icon
                          name={tab.icon}
                          className={`h-5 w-5 mr-2 ${
                            isActive ? tab.color : "text-gray-400 group-hover:text-gray-500"
                          }`}
                        />
                        {tab.label}
                        <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-medium ${
                          isActive
                            ? "bg-primary-100 text-primary-600"
                            : "bg-gray-100 text-gray-600"
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <Icon name="AlertCircle" className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Approved Tab Search */}
            {isApprovedTab && (
              <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-end w-full">
                    <div className="w-full sm:w-56">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Search By
                      </label>
                      <select
                        value={searchBy}
                        onChange={(e) => setSearchBy(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      >
                        <option value="all">Company / Quota Ref / VDR ID</option>
                        <option value="company_name">Company Name</option>
                        <option value="quota_ref">Quota Ref</option>
                        <option value="vdr_id">VDR ID</option>
                      </select>
                    </div>

                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Search
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Icon name="Search" className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Type company name, quota ref, or VDR ID"
                          className="block w-full pl-10 pr-10 py-2 rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm("")}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            aria-label="Clear search"
                          >
                            <Icon name="X" className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Filtering applies to Approved requests only.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Applications Table */}
            <div className="bg-white shadow rounded-lg">
              <SelectionRequestTable
                applications={applications}
                loading={loading}
                onViewDetails={handleViewDetails}
                onApprove={handleApprove}
                onReject={handleReject}
                processing={processing}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                pagination={pagination}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                activeTab={activeTab}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Approve Modal */}
      <ApproveModal
        show={showApproveModal}
        application={selectedApplication}
        processing={processing}
        onConfirm={confirmApprove}
        onCancel={cancelApprove}
      />

      {/* Reject Modal */}
      <RejectModal
         show={showRejectModal}
         application={selectedApplication}
         processing={processing}
         rejectionReasons={rejectionReasons}
         formData={rejectionForm}
         onConfirm={confirmReject}
         onCancel={cancelReject}
         onFormChange={updateRejectionForm}
       />
    </div>
  );
};

export default ClabSelectionRequests;
